import React, { useState, useRef } from 'react';
import api from '../api';

const inp = {
  fontSize: 13,
  background: '#f8fafc',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  color: '#1e293b',
  padding: '5px 8px',
  outline: 'none',
  width: '100%',
};

const sel = {
  ...inp,
  cursor: 'pointer',
};

function LineItemRow({ item, index, inventoryItems, onUpdate, onConfirm, onNewInventoryItem, confirmed, adding }) {
  const [editing, setEditing] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', unit: '', category: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const missing = item.unit_price == null && item.total_price == null;

  async function createInventoryItem() {
    if (!newItem.name || !newItem.unit) return;
    setSaving(true);
    setSaveError('');
    try {
      const { data } = await api.post('/inventory', {
        name: newItem.name,
        unit: newItem.unit,
        category: newItem.category || null,
        quantity_on_hand: 0,
      });
      onNewInventoryItem(data);
      onUpdate(index, { ...item, mapped_inventory_id: data.id });
      setCreatingNew(false);
      setNewItem({ name: '', unit: '', category: '' });
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Failed to create item');
    } finally {
      setSaving(false);
    }
  }

  // Find the selected inventory item object
  const selectedInvItem = inventoryItems.find(i => i.id === item.mapped_inventory_id) || null;
  const currentStock = selectedInvItem ? (parseFloat(selectedInvItem.quantity_on_hand) || 0) : null;
  const addingQty = parseFloat(item.quantity) || 0;
  const newTotal = currentStock != null ? currentStock + addingQty : null;

  function set(field, value) {
    const next = { ...item, [field]: value };
    if (field === 'unit_price' && value != null && next.quantity) {
      next.total_price = Math.round(value * next.quantity * 100) / 100;
    } else if (field === 'total_price' && value != null && next.quantity > 0) {
      next.unit_price = Math.round((value / next.quantity) * 100) / 100;
    }
    onUpdate(index, next);
  }

  if (confirmed) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', background: '#f0fdf4', border: '1px solid #86efac',
        borderRadius: 8, marginBottom: 8,
        animation: 'slideGreen 0.3s ease-out',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            width: 24, height: 24, borderRadius: '50%', background: '#22c55e',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontSize: 13, fontWeight: 700, flexShrink: 0,
          }}>✓</span>
          <div>
            <div style={{ color: '#166534', fontSize: 14, fontWeight: 600 }}>{item.item_name}</div>
            {selectedInvItem && (
              <div style={{ color: '#15803d', fontSize: 12 }}>→ {selectedInvItem.name}</div>
            )}
          </div>
        </div>
        <span style={{
          color: '#166534', fontSize: 12, background: '#dcfce7',
          padding: '3px 10px', borderRadius: 20, fontWeight: 500,
        }}>+{item.quantity_received ?? item.quantity} {item.unit} added</span>
      </div>
    );
  }

  return (
    <div style={{
      background: missing ? '#fffbeb' : 'white',
      border: `1px solid ${missing ? '#fcd34d' : '#e2e8f0'}`,
      borderRadius: 10, marginBottom: 10, overflow: 'hidden',
    }}>
      {/* Top row: scanned name + prices + buttons */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>
            {item.item_name || <span style={{ color: '#94a3b8' }}>Unknown item</span>}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <span>{item.quantity} {item.unit || ''}</span>
            {item.unit_price != null && <span>${Number(item.unit_price).toFixed(2)} / {item.unit || 'ea'}</span>}
            {item.total_price != null && <span style={{ color: '#0f172a', fontWeight: 700 }}>= ${Number(item.total_price).toFixed(2)}</span>}
            {missing && <span style={{ color: '#d97706', fontWeight: 500 }}>⚠️ prices missing</span>}
          </div>
          {item.discrepancy === 'qty_mismatch' && (
            <div style={{ fontSize: 11, color: '#d97706', marginTop: 2 }}>⚠️ PO had {item.quantity_ordered} ordered</div>
          )}
          {item.discrepancy === 'not_on_po' && (
            <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 2 }}>Not on purchase order</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            className="btn btn-ghost btn-sm"
            style={{ fontSize: 11, padding: '4px 10px' }}
            onClick={() => setEditing(e => !e)}
          >
            {editing ? 'Done' : '✏️ Edit'}
          </button>
          <button
            className="btn btn-primary btn-sm"
            style={{ fontSize: 11, padding: '4px 12px', minWidth: 60, opacity: adding ? 0.7 : 1 }}
            onClick={() => onConfirm(index)}
            disabled={adding}
          >
            {adding ? '...' : '✓ Add'}
          </button>
        </div>
      </div>

      {/* Inventory match section */}
      <div style={{ borderTop: '1px solid #f1f5f9', padding: '10px 14px', background: '#f8fafc' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 10, alignItems: 'end' }}>
          <div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Updates inventory item
            </div>
            <select
              style={sel}
              value={item.mapped_inventory_id || ''}
              onChange={e => {
                if (e.target.value === '__new__') {
                  setCreatingNew(true);
                  setNewItem({ name: item.item_name || '', unit: item.unit || '', category: '' });
                } else {
                  setCreatingNew(false);
                  onUpdate(index, { ...item, mapped_inventory_id: e.target.value || null });
                }
              }}
            >
              <option value="">— no match, skip —</option>
              <option value="__new__">＋ Create new inventory item</option>
              <option disabled>──────────────────</option>
              {inventoryItems.map(inv => (
                <option key={inv.id} value={inv.id}>{inv.name}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Current stock</div>
            <div style={{
              ...inp, textAlign: 'right', background: 'white',
              color: currentStock == null ? '#94a3b8' : '#0f172a',
            }}>
              {currentStock == null ? '—' : `${currentStock} ${selectedInvItem?.unit || ''}`}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>This order adds</div>
            <div style={{ ...inp, textAlign: 'right', background: 'white', color: '#0d9488', fontWeight: 700 }}>
              +{addingQty} {item.unit || ''}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>New total</div>
            <div style={{
              ...inp, textAlign: 'right', fontWeight: 700,
              background: newTotal != null ? '#f0fdf4' : 'white',
              border: newTotal != null ? '1px solid #86efac' : '1px solid #e2e8f0',
              color: newTotal != null ? '#166534' : '#94a3b8',
            }}>
              {newTotal != null ? `${newTotal} ${selectedInvItem?.unit || ''}` : '—'}
            </div>
          </div>
        </div>
      </div>

      {/* New inventory item form */}
      {creatingNew && (
        <div style={{ borderTop: '1px solid #e2e8f0', padding: '12px 14px', background: '#f0f9ff', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#0369a1' }}>＋ New inventory item</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto auto', gap: 8, alignItems: 'end' }}>
            <div>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase' }}>Item name *</div>
              <input style={inp} value={newItem.name} onChange={e => setNewItem(n => ({ ...n, name: e.target.value }))} placeholder="e.g. Bacon" />
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase' }}>Unit *</div>
              <input style={inp} value={newItem.unit} onChange={e => setNewItem(n => ({ ...n, unit: e.target.value }))} placeholder="lb, case…" />
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase' }}>Category</div>
              <input style={inp} value={newItem.category} onChange={e => setNewItem(n => ({ ...n, category: e.target.value }))} placeholder="Meat, Produce…" />
            </div>
            <button
              className="btn btn-primary btn-sm"
              style={{ fontSize: 12, whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
              onClick={createInventoryItem}
              disabled={saving || !newItem.name || !newItem.unit}
            >
              {saving ? '…' : 'Create'}
            </button>
            <button
              className="btn btn-ghost btn-sm"
              style={{ fontSize: 12, alignSelf: 'flex-end' }}
              onClick={() => { setCreatingNew(false); }}
            >
              Cancel
            </button>
          </div>
          {saveError && <div style={{ fontSize: 12, color: '#dc2626' }}>{saveError}</div>}
        </div>
      )}

      {/* Inline edit panel */}
      {editing && (
        <div style={{ borderTop: '1px solid #e2e8f0', padding: '10px 14px', background: '#fff', display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 8 }}>
          <div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase' }}>Item Name</div>
            <input style={inp} value={item.item_name || ''} onChange={e => set('item_name', e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase' }}>Qty</div>
            <input style={inp} type="number" min="0" step="0.01" value={item.quantity ?? ''} onChange={e => set('quantity', e.target.value === '' ? null : parseFloat(e.target.value))} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase' }}>Unit</div>
            <input style={inp} value={item.unit || ''} onChange={e => set('unit', e.target.value)} placeholder="lb" />
          </div>
          <div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase' }}>Unit Price</div>
            <input
              style={{ ...inp, borderColor: missing ? '#fbbf24' : '#cbd5e1' }}
              type="number" min="0" step="0.01"
              value={item.unit_price ?? ''}
              onChange={e => set('unit_price', e.target.value === '' ? null : parseFloat(e.target.value))}
              placeholder="0.00"
            />
          </div>
          <div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase' }}>Total</div>
            <input
              style={{ ...inp, borderColor: missing ? '#fbbf24' : '#cbd5e1' }}
              type="number" min="0" step="0.01"
              value={item.total_price ?? ''}
              onChange={e => set('total_price', e.target.value === '' ? null : parseFloat(e.target.value))}
              placeholder="0.00"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function InvoiceScanner({ onComplete }) {
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [confirmed, setConfirmed] = useState({});
  const [adding, setAdding] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [confirmAllDone, setConfirmAllDone] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef();

  function updateItem(index, next) {
    setLineItems(prev => prev.map((item, i) => i === index ? next : item));
  }

  async function confirmOne(index) {
    const item = lineItems[index];
    if (!item?.id) return;
    setAdding(prev => ({ ...prev, [index]: true }));
    try {
      await api.post(`/invoices/${result.id}/receive`, {
        received_items: [{
          line_item_id: item.id,
          inventory_item_id: item.mapped_inventory_id || null,
          quantity_received: parseFloat(item.quantity) || 0,
          received: true,
        }],
      });
      setLineItems(prev => prev.map((it, i) => i === index ? { ...it, quantity_received: it.quantity } : it));
      setConfirmed(prev => ({ ...prev, [index]: true }));
      // Refresh inventory counts
      loadInventory();
    } catch (err) {
      setConfirmError(`Failed to add ${item.item_name}: ${err.response?.data?.error || err.message}`);
    } finally {
      setAdding(prev => ({ ...prev, [index]: false }));
    }
  }

  async function confirmAll() {
    setConfirming(true);
    setConfirmError('');
    const unconfirmedItems = lineItems.filter((_, i) => !confirmed[i]);
    try {
      await api.post(`/invoices/${result.id}/receive`, {
        received_items: unconfirmedItems.map(item => ({
          line_item_id: item.id,
          inventory_item_id: item.mapped_inventory_id || null,
          quantity_received: parseFloat(item.quantity) || 0,
          received: true,
        })),
      });
      const newConfirmed = {};
      lineItems.forEach((_, i) => { newConfirmed[i] = true; });
      setConfirmed(newConfirmed);
      setConfirmAllDone(true);
    } catch (err) {
      setConfirmError(err.response?.data?.error || 'Failed to confirm — please try again.');
    } finally {
      setConfirming(false);
    }
  }

  async function loadInventory() {
    try {
      const { data } = await api.get('/inventory?limit=500');
      const items = Array.isArray(data) ? data : (data.items || []);
      setInventoryItems(items.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (_) {}
  }

  // Auto-map scanned items to inventory by fuzzy name match
  function autoMap(scannedItems, invItems) {
    return scannedItems.map(item => {
      if (item.inventory_item_id) {
        return { ...item, mapped_inventory_id: item.inventory_item_id };
      }
      const nameLower = (item.item_name || '').toLowerCase();
      const words = nameLower.split(/[\s,]+/).filter(w => w.length > 3);
      const match = invItems.find(inv => {
        const invLower = inv.name.toLowerCase();
        return words.some(w => invLower.includes(w)) || invLower.split(/[\s,]+/).some(w => w.length > 3 && nameLower.includes(w));
      });
      return { ...item, mapped_inventory_id: match?.id || null };
    });
  }

  async function handleFile(file) {
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setStatus('uploading');
    setError('');
    const formData = new FormData();
    formData.append('image', file);
    try {
      setStatus('processing');
      const [{ data }, invResponse] = await Promise.all([
        api.post('/invoices/scan', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
        api.get('/inventory?limit=500').catch(() => ({ data: [] })),
      ]);
      const invItems = Array.isArray(invResponse.data) ? invResponse.data : (invResponse.data?.items || []);
      const sortedInv = invItems.sort((a, b) => a.name.localeCompare(b.name));
      setInventoryItems(sortedInv);
      setResult(data);
      setLineItems(autoMap(data.line_items || [], sortedInv));
      setConfirmed({});
      setAdding({});
      setConfirmAllDone(false);
      setStatus('done');
      onComplete && onComplete(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Scan failed — please try again.');
      setStatus('error');
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  function reset() {
    setStatus('idle');
    setResult(null);
    setError('');
    setPreview(null);
    setConfirmed({});
    setAdding({});
    setConfirmAllDone(false);
    setConfirmError('');
  }

  if (status === 'done' && result) {
    const confirmedCount = Object.values(confirmed).filter(Boolean).length;
    const remaining = lineItems.filter((_, i) => !confirmed[i]);
    const missingPrices = lineItems.some(i => i.unit_price == null && i.total_price == null);

    return (
      <div>
        {/* Header */}
        <div className="card mb-4">
          <div className="card-body" style={{ padding: '14px 18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{result.vendor_name}</span>
                {result.purchase_order && <span className="badge badge-teal" style={{ marginLeft: 8 }}>Matched PO</span>}
              </div>
              <span style={{ fontSize: 12, color: '#64748b' }}>{lineItems.length} items · {confirmedCount} added to inventory</span>
            </div>
            {missingPrices && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#d97706', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 10px' }}>
                ⚠️ Some items are missing prices — click ✏️ Edit on those rows to enter them.
              </div>
            )}
          </div>
        </div>

        {/* Line items */}
        <div style={{ marginBottom: 16 }}>
          {lineItems.map((item, i) => (
            <LineItemRow
              key={item.id || i}
              item={item}
              index={i}
              inventoryItems={inventoryItems}
              onUpdate={updateItem}
              onConfirm={confirmOne}
              onNewInventoryItem={newInv => setInventoryItems(prev => [...prev, newInv].sort((a, b) => a.name.localeCompare(b.name)))}
              confirmed={!!confirmed[i]}
              adding={!!adding[i]}
            />
          ))}
        </div>

        {confirmError && <div className="alert alert-error mb-3">{confirmError}</div>}

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 14, borderTop: '1px solid #e2e8f0' }}>
          <button className="btn btn-ghost btn-sm" onClick={reset}>Scan Another</button>
          {confirmAllDone ? (
            <span style={{ color: '#166534', fontWeight: 600, fontSize: 14 }}>✓ All items added to inventory</span>
          ) : remaining.length > 0 ? (
            <button
              className="btn btn-primary"
              onClick={confirmAll}
              disabled={confirming}
              style={{ minWidth: 230, fontSize: 14 }}
            >
              {confirming ? 'Adding...' : `✓ Add All ${remaining.length} Items to Inventory`}
            </button>
          ) : (
            <span style={{ color: '#166534', fontWeight: 600, fontSize: 14 }}>✓ All items added to inventory</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">📷 Scan Invoice</div>
      <div className="card-body">
        {error && <div className="alert alert-error mb-3">{error}</div>}

        {status === 'idle' || status === 'error' ? (
          <>
            <label
              htmlFor="invoice-image-input"
              className={`upload-zone${dragOver ? ' drag-over' : ''}`}
              style={{ cursor: 'pointer', display: 'block' }}
              onDrop={onDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
            >
              <div className="upload-icon">📸</div>
              <p className="upload-text">
                <strong>Tap to take or choose a photo</strong><br />
                of your invoice or delivery receipt
              </p>
              <p className="text-xs text-gray mt-2">JPG, PNG — up to 20MB</p>
            </label>
            <input
              id="invoice-image-input"
              ref={inputRef}
              type="file"
              accept="image/*"
              style={{ position: 'absolute', opacity: 0, width: 1, height: 1, pointerEvents: 'none' }}
              onChange={(e) => { if (e.target.files[0]) handleFile(e.target.files[0]); e.target.value = ''; }}
            />
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: 40 }}>
            {preview && (
              <img src={preview} alt="Invoice" style={{ maxHeight: 180, borderRadius: 8, marginBottom: 16, border: '1px solid #e2e8f0' }} />
            )}
            <div style={{ color: '#64748b' }}>
              {status === 'uploading' ? '⬆️ Uploading...' : '🔍 Reading invoice...'}
            </div>
            <div className="progress-bar mt-2">
              <div className="progress-fill good" style={{ width: status === 'uploading' ? '30%' : '80%', transition: 'width 1.2s' }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
